// src/features/task/TaskTypeStatusesEditor.tsx
// The status half of a task type's lifecycle: the named states a task of this
// type can sit in, in board order.
//
// A custom status is a label over a base status, not a new one. The `category`
// is the base status it counts as, and it is the field boards and stats group
// by — so it is shown on every row, not buried in an edit panel.
//
// An empty list used to render an empty editor, which said nothing about what
// would actually happen: a type with no statuses of its own follows the set its
// scope offers, and "nothing here" and "nothing anywhere" looked identical. So
// when the type defines none and the scope offers a set, that set is shown —
// read-only, visibly not this type's, and named for where it came from.
//
// There is deliberately no "inherit from parent" switch. Absence of a value
// already means inherit; a second control would be a second source of truth and
// would drift from the chain that actually decides. What the type gains instead
// is one explicit action — copy the inherited set and own it from then on —
// which is stated as a consequence before it happens, not reported after.
//
// Note the direction of the rule: statuses resolve type-first. A type that
// declares its own is never overridden by anything in its scope. Nothing here
// may suggest otherwise.

import { useMemo, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Badge } from '@react/components';
import { Select } from '@/components/Select';
import { ColorPicker } from '@/components/ColorPicker';
import { IconPicker, ProjectIcon } from '@/components/IconPicker';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormRow } from '@/components/form';
import type { Entity } from '@core/types/entity';
import type { ResolvedStatusSet, TaskStatus, TaskStatusDefinition } from '@core/types/task';
import { TASK_TYPE_COLORS } from '@/features/task/TaskTypeForm';
import { OriginLine, useOriginSentence } from '@/features/task/InheritanceNotice';
import {
  BASE_TASK_STATUSES,
  moveStatusDefinition,
  slugifyStatus,
  sortStatusDefinitions,
  statusLabelKey,
  withNormalizedPositions,
} from '@/features/task/taskLifecycle';

export interface TaskTypeStatusesEditorProps {
  value: TaskStatusDefinition[];
  onChange: (next: TaskStatusDefinition[]) => void;
  disabled?: boolean;
  /**
   * The status set this type's scope offers, or null when it offers none.
   * Already filtered by the caller so an empty set never arrives as a set.
   */
  inherited?: ResolvedStatusSet | null;
  /** The scope the resolver was asked about — origins compare against it. */
  askedScope?: Entity;
  /** The resolver is still answering; say so rather than showing an empty set. */
  isResolving?: boolean;
  /** The resolver failed. Not the same as "nothing is configured". */
  resolveError?: string | null;
  onRetryResolve?: () => void;
}

const CHEVRON_UP = 'M18 15l-6-6-6 6';
const CHEVRON_DOWN = 'M6 9l6 6 6-6';

function Chevron({ path }: { path: string }) {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

/**
 * A row of the inherited set. Deliberately not the editable row: no controls,
 * no affordances, and the whole block is marked read-only so a screen reader
 * reaches the same conclusion the eye does. Read-only is not disabled — these
 * statuses are live and will be used, they simply are not this type's to edit.
 */
function InheritedStatusRow({ def }: { def: TaskStatusDefinition }) {
  const { t } = useI18n();
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2">
      <span
        className="h-3.5 w-3.5 shrink-0 rounded-full border border-border"
        style={def.color ? { backgroundColor: def.color } : undefined}
        aria-hidden="true"
      />
      {def.icon ? (
        <ProjectIcon iconId={def.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : null}
      <span className="truncate text-sm font-medium text-muted-foreground">
        {def.display_name || def.slug}
      </span>
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
        {def.slug}
      </code>
      <Badge variant="secondary" size="sm">
        {statusLabelKey(def.category)
          ? t(statusLabelKey(def.category))
          : t('task.lifecycle.statuses.noCategory')}
      </Badge>
    </li>
  );
}

export function TaskTypeStatusesEditor({
  value,
  onChange,
  disabled,
  inherited,
  askedScope,
  isResolving,
  resolveError,
  onRetryResolve,
}: TaskTypeStatusesEditorProps) {
  const { t } = useI18n();

  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<TaskStatusDefinition | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftSlug, setDraftSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [draftCategory, setDraftCategory] = useState<string>('TASK_STATUS_OPEN');
  const [addError, setAddError] = useState<string | null>(null);
  // Both ways of leaving inheritance route through one confirmation, because
  // both have the same consequence and it is the consequence that needs saying.
  const [pendingAdopt, setPendingAdopt] = useState<'copy' | 'define' | null>(null);

  const ordered = useMemo(() => sortStatusDefinitions(value), [value]);

  const ownsStatuses = ordered.length > 0;
  const inheritedDefs = useMemo(
    () => sortStatusDefinitions(inherited?.status_definitions || []),
    [inherited],
  );
  const isInheriting = !ownsStatuses && inheritedDefs.length > 0;

  // Wording for the line that names the source, reused by the confirmation so
  // the dialog and the banner cannot describe two different records.
  const originWords = useOriginSentence(inherited?.origin, askedScope, 'statuses');

  const categoryOptions = useMemo(
    () => BASE_TASK_STATUSES.map((status) => ({ value: status, label: t(statusLabelKey(status)) })),
    [t],
  );

  const patch = (index: number, changes: Partial<TaskStatusDefinition>) => {
    onChange(
      withNormalizedPositions(ordered.map((def, i) => (i === index ? { ...def, ...changes } : def))),
    );
  };

  const move = (index: number, delta: number) => {
    onChange(moveStatusDefinition(ordered, index, delta));
  };

  const confirmRemove = () => {
    if (!pendingRemoval) return;
    onChange(withNormalizedPositions(ordered.filter((def) => def.slug !== pendingRemoval.slug)));
    if (expandedSlug === pendingRemoval.slug) setExpandedSlug(null);
    setPendingRemoval(null);
  };

  const effectiveDraftSlug = slugEdited ? draftSlug : slugifyStatus(draftName);

  /** Everything `add` needs once the draft is known to be valid. */
  const commitDraft = () => {
    const slug = effectiveDraftSlug;
    onChange(
      withNormalizedPositions([
        ...ordered,
        {
          slug,
          display_name: draftName.trim(),
          description: '',
          category: draftCategory as TaskStatus,
          position: ordered.length,
        },
      ]),
    );
    setDraftName('');
    setDraftSlug('');
    setSlugEdited(false);
    setAddError(null);
    setExpandedSlug(slug);
  };

  const add = () => {
    const slug = effectiveDraftSlug;
    const name = draftName.trim();
    if (!name) {
      setAddError(t('task.lifecycle.statuses.errorNameRequired'));
      return;
    }
    if (!slug || !/^[a-z][a-z0-9_]*$/.test(slug)) {
      setAddError(t('task.statusDef.slugFormat'));
      return;
    }
    if (ordered.some((def) => def.slug === slug)) {
      setAddError(t('task.lifecycle.statuses.errorSlugTaken'));
      return;
    }
    // While the type is following an inherited set, this one status would
    // become the whole of its lifecycle. That is the same decision as copying
    // the set, taken by a quieter route, so it asks the same question.
    if (isInheriting) {
      setAddError(null);
      setPendingAdopt('define');
      return;
    }
    commitDraft();
  };

  /**
   * Copy the inherited set onto this type. From here on the type answers for
   * its own statuses and stops following the scope — later edits to the source
   * record will not reach it. Only ever reached through the confirmation.
   */
  const adoptInherited = () => {
    onChange(
      withNormalizedPositions(
        inheritedDefs.map((def, index) => ({ ...def, position: index })),
      ),
    );
  };

  const confirmAdopt = () => {
    if (pendingAdopt === 'copy') adoptInherited();
    if (pendingAdopt === 'define') commitDraft();
    setPendingAdopt(null);
  };

  const inputClass =
    'w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60';

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('task.lifecycle.statuses.description')}</p>

      {/* A type that declares its own statuses is never overridden — statuses
          resolve type-first, unlike every other default on this form. Say which
          way the rule runs, and name what is being set aside where there is
          something to name. */}
      {ownsStatuses && (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {inheritedDefs.length > 0
              ? t('task.inheritance.statuses.ownWithScope', {
                  source: originWords.source,
                  name: originWords.name,
                })
              : t('task.inheritance.statuses.own')}
          </p>
        </div>
      )}

      {ordered.length === 0 ? (
        isResolving ? (
          <div
            className="space-y-2"
            role="status"
            aria-live="polite"
            aria-label={t('task.inheritance.resolving')}
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : resolveError ? (
          <div className="alert-error space-y-2 rounded-md border px-4 py-3">
            <p className="text-sm">{t('task.inheritance.statuses.resolveFailed')}</p>
            <p className="text-xs opacity-80">{resolveError}</p>
            {onRetryResolve && (
              <Button size="sm" variant="secondary" onClick={onRetryResolve}>
                {t('common.retry')}
              </Button>
            )}
          </div>
        ) : isInheriting ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" size="sm">
                {t('task.inheritance.badge')}
              </Badge>
              <p className="text-sm font-medium text-foreground">
                {t('task.inheritance.statuses.heading', { count: inheritedDefs.length })}
              </p>
            </div>

            <OriginLine origin={inherited?.origin} askedScope={askedScope} kind="statuses" />

            {/* Read-only, not disabled: these statuses are what tasks of this
                type will actually get. They are simply not this type's to edit
                from here — the record that owns them is one link away. */}
            {/* The list carries "read-only" in its accessible name rather than
                in an aria-readonly attribute, which a list role does not
                support. Nothing inside it is focusable, so the absence of
                controls says the same thing to the keyboard. */}
            <ul className="space-y-2" aria-label={t('task.inheritance.statuses.listLabel')}>
              {inheritedDefs.map((def) => (
                <InheritedStatusRow key={def.slug} def={def} />
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="secondary"
                disabled={disabled}
                onClick={() => setPendingAdopt('copy')}
              >
                {t('task.inheritance.statuses.adopt')}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t('task.inheritance.statuses.adoptHint')}
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border px-4 py-6 text-center">
            <p className="text-sm font-medium text-foreground">
              {t('task.inheritance.statuses.noneAnywhere')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('task.lifecycle.statuses.emptyHint')}
            </p>
          </div>
        )
      ) : (
        <ul className="space-y-2">
          {ordered.map((def, index) => {
            const isExpanded = expandedSlug === def.slug;
            return (
              <li key={def.slug} className="rounded-md border border-border bg-background">
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span
                    className="h-3.5 w-3.5 shrink-0 rounded-full border border-border"
                    style={def.color ? { backgroundColor: def.color } : undefined}
                    aria-hidden="true"
                  />
                  {def.icon ? (
                    <ProjectIcon iconId={def.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : null}
                  <span className="truncate text-sm font-medium text-foreground">
                    {def.display_name || def.slug}
                  </span>
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                    {def.slug}
                  </code>
                  <Badge variant="secondary" size="sm">
                    {statusLabelKey(def.category)
                      ? t(statusLabelKey(def.category))
                      : t('task.lifecycle.statuses.noCategory')}
                  </Badge>

                  <div className="ml-auto flex shrink-0 items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={disabled || index === 0}
                      aria-label={t('task.statusDef.moveUp')}
                      title={t('task.statusDef.moveUp')}
                      onClick={() => move(index, -1)}
                    >
                      <Chevron path={CHEVRON_UP} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={disabled || index === ordered.length - 1}
                      aria-label={t('task.statusDef.moveDown')}
                      title={t('task.statusDef.moveDown')}
                      onClick={() => move(index, 1)}
                    >
                      <Chevron path={CHEVRON_DOWN} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedSlug(isExpanded ? null : def.slug)}
                    >
                      {isExpanded ? t('common.close') : t('common.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      disabled={disabled}
                      onClick={() => setPendingRemoval(def)}
                    >
                      {t('common.remove')}
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="space-y-1 border-t border-border px-3 py-2">
                    <FormRow
                      label={t('task.statusDef.slug')}
                      labelWidth="md"
                      hint={t('task.lifecycle.statuses.slugLocked')}
                    >
                      <code className="inline-block rounded bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
                        {def.slug}
                      </code>
                    </FormRow>

                    <FormRow
                      label={t('task.statusDef.displayName')}
                      labelWidth="md"
                      htmlFor={`status-name-${def.slug}`}
                    >
                      <input
                        id={`status-name-${def.slug}`}
                        type="text"
                        className={inputClass}
                        value={def.display_name}
                        disabled={disabled}
                        autoCapitalize="off"
                        onChange={(e) => patch(index, { display_name: e.target.value })}
                      />
                    </FormRow>

                    <FormRow
                      label={t('task.statusDef.category')}
                      labelWidth="md"
                      hint={t('task.lifecycle.statuses.categoryHint')}
                    >
                      <Select
                        value={String(def.category ?? '')}
                        onChange={(e) => patch(index, { category: e.target.value as TaskStatus })}
                        options={categoryOptions}
                        placeholder={t('task.statusDef.selectCategory')}
                        disabled={disabled}
                        fullWidth={false}
                        className="w-56"
                        aria-label={t('task.statusDef.category')}
                      />
                    </FormRow>

                    <FormRow label={t('task.statusDef.color')} labelWidth="md">
                      <div className="flex flex-wrap items-center gap-3">
                        <ColorPicker
                          options={TASK_TYPE_COLORS}
                          value={def.color || ''}
                          onChange={(color) => patch(index, { color })}
                        />
                        {def.color ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={disabled}
                            onClick={() => patch(index, { color: '' })}
                          >
                            {t('common.reset')}
                          </Button>
                        ) : null}
                      </div>
                    </FormRow>

                    <FormRow label={t('task.statusDef.icon')} labelWidth="md">
                      <IconPicker
                        value={def.icon || ''}
                        onChange={(icon) => patch(index, { icon })}
                      />
                    </FormRow>

                    <FormRow
                      label={t('task.lifecycle.statuses.descriptionLabel')}
                      labelWidth="md"
                      htmlFor={`status-desc-${def.slug}`}
                    >
                      <input
                        id={`status-desc-${def.slug}`}
                        type="text"
                        className={inputClass}
                        value={def.description || ''}
                        disabled={disabled}
                        placeholder={t('task.statusDef.descriptionPlaceholder')}
                        onChange={(e) => patch(index, { description: e.target.value })}
                      />
                    </FormRow>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="space-y-3 border-t border-border pt-4">
        <h4 className="text-sm font-medium text-foreground">{t('task.statusDef.addNew')}</h4>
        {/* Adding the first status while inheriting is not an addition — it is
            a replacement of the whole inherited set. Say so before the field,
            not in an error after the button. */}
        {isInheriting && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('task.inheritance.statuses.addWhileInheriting')}
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="new-status-name" className="mb-1.5 block text-sm text-muted-foreground">
              {t('task.statusDef.displayName')}
            </label>
            <input
              id="new-status-name"
              type="text"
              className={inputClass}
              value={draftName}
              disabled={disabled}
              autoCapitalize="off"
              placeholder={t('task.statusDef.displayNamePlaceholder')}
              onChange={(e) => {
                setDraftName(e.target.value);
                setAddError(null);
              }}
            />
          </div>
          <div>
            <label htmlFor="new-status-slug" className="mb-1.5 block text-sm text-muted-foreground">
              {t('task.statusDef.slug')}
            </label>
            <input
              id="new-status-slug"
              type="text"
              className={`${inputClass} font-mono`}
              value={effectiveDraftSlug}
              disabled={disabled}
              autoCapitalize="off"
              placeholder="awaiting_qa"
              onChange={(e) => {
                setSlugEdited(true);
                setDraftSlug(slugifyStatus(e.target.value));
                setAddError(null);
              }}
            />
          </div>
          <div>
            <Select
              id="new-status-category"
              label={t('task.statusDef.category')}
              value={draftCategory}
              onChange={(e) => setDraftCategory(e.target.value)}
              options={categoryOptions}
              disabled={disabled}
            />
          </div>
        </div>
        {addError ? <p className="text-sm text-destructive">{addError}</p> : null}
        <div className="flex justify-end">
          <Button size="sm" variant="secondary" disabled={disabled} onClick={add}>
            {t('task.statusDef.add')}
          </Button>
        </div>
      </div>

      {/* One question for both routes out of inheritance. It names the source
          being left, and states the consequence — later changes to that record
          stop reaching this type — before the change, not after it. */}
      <ConfirmDialog
        open={pendingAdopt !== null}
        title={
          pendingAdopt === 'define'
            ? t('task.inheritance.statuses.confirmDefineTitle')
            : t('task.inheritance.statuses.confirmCopyTitle')
        }
        description={
          pendingAdopt === 'define'
            ? t('task.inheritance.statuses.confirmDefineBody', {
                name: draftName.trim(),
                source: originWords.source,
                origin: originWords.name,
                count: inheritedDefs.length,
              })
            : t('task.inheritance.statuses.confirmCopyBody', {
                count: inheritedDefs.length,
                source: originWords.source,
                origin: originWords.name,
              })
        }
        confirmLabel={
          pendingAdopt === 'define'
            ? t('task.inheritance.statuses.confirmDefineAction')
            : t('task.inheritance.statuses.confirmCopyAction')
        }
        onConfirm={confirmAdopt}
        onCancel={() => setPendingAdopt(null)}
      />

      <ConfirmDialog
        open={!!pendingRemoval}
        title={t('task.lifecycle.statuses.removeTitle')}
        description={t('task.lifecycle.statuses.removeDescription', {
          name: pendingRemoval?.display_name || pendingRemoval?.slug || '',
        })}
        confirmLabel={t('common.remove')}
        destructive
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemoval(null)}
      />
    </div>
  );
}

export default TaskTypeStatusesEditor;
